import { createRemoteClientRuntime, type RemoteClient, type RemoteConnectionOptions } from "../remote/remote-client"
import { createRemoteFakeRegistry, type RemoteFakeDefinition, type RemoteFakeRegistrar } from "../remote/remote-fakes"
import {
  createPresenterRuntime,
  PresenterUnhandledResponseError,
  type PresentationResponseDefinition,
  type PresentationResponseHandler,
  type PresentationNotificationOptions,
  type Presenter,
} from "../presentation/presenter"
import { sessionExpiredResponse } from "../presentation/responses/session-expired"
import { Session, type SessionOptions } from "../session/session"
import { createMemoryStorage, type Storage } from "../storage/storage"
import { ViewState } from "../view-state/view-state"
import { createLocalization, type Localization, type LocalizationOptions } from "../localization/localization"
import {
  createApplicationAuthenticatedRootRuntime,
  type ApplicationAuthenticatedState,
} from "./application-authenticated-root"
import {
  defineApplication,
  type ApplicationDefinition,
  type ApplicationPlugin,
  type ApplicationPluginAvailability,
} from "./composition"
import type { ApplicationScope } from "./plugin-runtime"
import {
  createApplicationTelemetryRuntime,
  type ApplicationTelemetry,
  type ApplicationTelemetryOptions,
} from "../telemetry/telemetry"
import {
  createMessenger,
  type AnyMessageDefinition,
  type MessageDefinition,
  type Messenger,
} from "../messaging/messenger"
import {
  createDataInvalidation,
  normalizeDataDependencies,
  type DataDependency,
  type DataDependencyInput,
  type DataInvalidation,
  type DataInvalidationRegistrar,
} from "../data/data-invalidation"
import {
  createApplicationAuthentication,
  type ApplicationAuthenticationOptions,
  type ApplicationAuthenticationResult,
} from "./application-authentication"
import type { LeaveGuard } from "./application-leave-guard"

const emptyApplicationDefinition = defineApplication({ plugins: [] })

export type { ApplicationAuthenticatedState } from "./application-authenticated-root"

export type ApplicationLocalizationOptions = Omit<LocalizationOptions, "storage">

export interface ApplicationOptions {
  definition?: ApplicationDefinition
  remote: RemoteConnectionOptions
  /** Initial fake endpoints available before authentication and plugin activation. */
  remoteFakes?: readonly RemoteFakeDefinition[]
  session?: SessionOptions
  authentication?: ApplicationAuthenticationOptions | false
  storage?: Storage
  localization?: ApplicationLocalizationOptions
  telemetry?: ApplicationTelemetryOptions
}

export interface Application {
  readonly branding: ApplicationDefinition["branding"]
  readonly plugins: readonly ApplicationPlugin[]
  readonly presenter: Presenter
  readonly remote: RemoteClient
  readonly rootScope: ApplicationScope
  readonly session: Session
  readonly storage: Storage
  readonly viewState: ViewState
  readonly localization: Localization
  readonly telemetry: ApplicationTelemetry
  readonly messenger: Messenger
  readonly dataInvalidation: DataInvalidation
  readonly authenticatedState: ApplicationAuthenticatedState

  subscribeAuthenticatedState(listener: () => void): () => void
  whenAuthenticated(): Promise<void>
  /** Retries failed authenticated Root loading for the current access context. */
  retryAuthenticatedRoot(): Promise<void>
  getPluginAvailability(plugin: ApplicationPlugin | string): ApplicationPluginAvailability | null
  isPluginAuthorized(plugin: ApplicationPlugin | string): boolean
  activatePlugin(plugin: ApplicationPlugin | string): Promise<ApplicationScope>
  /** Presents one shared login challenge and waits for authenticated Root loading. */
  requestAuthentication(signal?: AbortSignal): Promise<ApplicationAuthenticationResult>
  /**
   * Signs out after every registered sign-out guard allows it, for example the leave guards of documents
   * with unsaved changes. Resolves false, and keeps the session, when a guard cancels. Concurrent calls
   * share one attempt. `session.signOut()` remains the immediate, unguarded sign-out.
   */
  signOut(): Promise<boolean>
  /** Registers a guard that `signOut()` consults, in registration order, before it clears the session. */
  registerSignOutGuard(guard: LeaveGuard): () => void
  /** Releases application-owned runtime resources. Safe to call more than once. */
  dispose(): Promise<void>
}

export class ApplicationPluginUnauthorizedError extends Error {
  constructor(public readonly pluginId: string) {
    super(`Application plugin is not authorized: ${pluginId}.`)
    this.name = "ApplicationPluginUnauthorizedError"
  }
}

interface PluginRuntimeState {
  initialization?: Promise<ApplicationScope>
}

interface ApplicationPreloads {
  count: number
}

const applicationPreloads = new WeakMap<Application, ApplicationPreloads>()

/**
 * @internal Runs route preloading work. A 401 it causes does not offer the login challenge; the next 401
 * outside preloading does.
 */
export async function runApplicationPreload<T>(application: Application, operation: () => Promise<T>): Promise<T> {
  const preloads = applicationPreloads.get(application)
  if (preloads == null) return operation()
  preloads.count += 1
  try {
    return await operation()
  } finally {
    preloads.count -= 1
  }
}

export function createApplication(options: ApplicationOptions): Application {
  const definition = options.definition ?? emptyApplicationDefinition
  const plugins = new Map(definition.plugins.map((plugin) => [plugin.id, plugin]))
  const pluginStates = new Map<string, PluginRuntimeState>()
  const remoteFakes = createRemoteFakeRegistry()
  if (options.remoteFakes?.length) {
    remoteFakes.createRegistrar(null).register(...options.remoteFakes)
  }
  const telemetryRuntime = createApplicationTelemetryRuntime(options.telemetry)
  const presenterRuntime = createPresenterRuntime((error, type, namespace) => {
    telemetryRuntime.telemetry.error("Presenter notification failed.", error, {
      "florisoft.error.source": "presenter",
      "presentation.type": type,
      "presentation.namespace": namespace,
      "presentation.mode": "notify",
    })
  })
  const dataInvalidation = createDataInvalidation()
  const dataInvalidationBindings = createDataInvalidationBindings()
  const messenger = createMessenger({
    beforeEmit(message, payload) {
      const dependencies: DataDependency[] = []
      const invalidates = (
        message as AnyMessageDefinition & {
          readonly invalidates?: (payload: unknown) => readonly DataDependency[]
        }
      ).invalidates
      if (invalidates != null) {
        try {
          dependencies.push(...invalidates(payload))
        } catch (error) {
          reportMessageInvalidationError(telemetryRuntime.telemetry, message.type, error)
        }
      }

      const bound = dataInvalidationBindings.resolve(message.type, payload)
      dependencies.push(...bound.dependencies)
      for (const error of bound.errors) {
        reportMessageInvalidationError(telemetryRuntime.telemetry, message.type, error)
      }

      if (dependencies.length > 0) {
        try {
          dataInvalidation.invalidate(dependencies)
        } catch (error) {
          reportMessageInvalidationError(telemetryRuntime.telemetry, message.type, error)
        }
      }
    },
  })
  const remoteRuntime = createRemoteClientRuntime(
    options.remote,
    remoteFakes,
    presenterRuntime.resolver,
    telemetryRuntime,
    messenger
  )
  const storage = options.storage ?? createMemoryStorage()
  const localization = createLocalization({
    ...options.localization,
    storage,
    resources: [
      ...(options.localization?.resources ?? []),
      ...definition.plugins.flatMap((plugin) => plugin.localization ?? []),
    ],
  })
  const viewState = new ViewState(storage)
  const remote = remoteRuntime.root
  const session = new Session(remote, storage, options.session)
  const authenticatedRoot = createApplicationAuthenticatedRootRuntime(definition.plugins, session, remote)
  const rootScope: ApplicationScope = Object.freeze({
    id: null,
    parent: null,
    presenter: presenterRuntime.root,
    remote: remoteRuntime.root,
    localization,
    telemetry: telemetryRuntime.telemetry,
    messenger,
    dataInvalidation,
  })
  let disposePromise: Promise<void> | undefined

  const getPlugin = (pluginOrId: ApplicationPlugin | string) => {
    const id = typeof pluginOrId === "string" ? pluginOrId : pluginOrId.id
    const plugin = plugins.get(id)
    if (plugin == null) {
      throw new Error(`Application plugin is not registered: ${id}.`)
    }
    return plugin
  }

  const getPluginState = (pluginId: string) => {
    let state = pluginStates.get(pluginId)
    if (state == null) {
      state = {}
      pluginStates.set(pluginId, state)
    }
    return state
  }

  function isPluginAuthorized(pluginOrId: ApplicationPlugin | string): boolean {
    const plugin = getPlugin(pluginOrId)
    if (plugin.parentId != null && !isPluginAuthorized(plugin.parentId)) return false
    if (!authenticatedRoot.isPluginAvailable(plugin)) return false
    return plugin.isAuthorized?.(Object.freeze({ application, session })) ?? true
  }

  function activatePlugin(pluginOrId: ApplicationPlugin | string): Promise<ApplicationScope> {
    let plugin: ApplicationPlugin
    try {
      plugin = getPlugin(pluginOrId)
    } catch (error) {
      return Promise.reject(error)
    }
    if (!authenticatedRoot.requiresAuthenticatedRoot(plugin)) {
      return activatePluginAfterAuthenticated(plugin)
    }
    return authenticatedRoot.whenAuthenticated().then(() => activatePluginAfterAuthenticated(plugin))
  }

  function activatePluginAfterAuthenticated(plugin: ApplicationPlugin): Promise<ApplicationScope> {
    if (!isPluginAuthorized(plugin)) {
      return Promise.reject(new ApplicationPluginUnauthorizedError(plugin.id))
    }
    const id = plugin.id

    const state = getPluginState(id)
    if (state.initialization != null) return state.initialization

    let resolveInitialization!: (scope: ApplicationScope) => void
    let rejectInitialization!: (reason?: unknown) => void
    const initialization = new Promise<ApplicationScope>((resolve, reject) => {
      resolveInitialization = resolve
      rejectInitialization = reject
    })
    state.initialization = initialization

    void (async () => {
      let presenterRegistrations: RegistrationTracker<Presenter> | undefined
      let remoteFakeRegistrations: RegistrationTracker<RemoteFakeRegistrar> | undefined
      let dataInvalidationRegistrations: RegistrationTracker<DataInvalidationRegistrar> | undefined
      try {
        if (localization.snapshot.status !== "ready") await localization.ready
        const parentScope = plugin.parentId == null ? rootScope : await activatePlugin(plugin.parentId)
        const scopeIds = getScopeIds(plugin, plugins)
        presenterRegistrations = trackPresenterRegistrations(presenterRuntime.createScoped(scopeIds))
        remoteFakeRegistrations = trackRemoteFakeRegistrations(remoteFakes.createRegistrar(plugin.id))
        dataInvalidationRegistrations = trackDataInvalidationRegistrations(dataInvalidationBindings.registrar)
        const scope: ApplicationScope = Object.freeze({
          id: plugin.id,
          parent: parentScope,
          presenter: presenterRegistrations.value,
          remote: remoteRuntime.createScoped(scopeIds),
          localization,
          telemetry: telemetryRuntime.telemetry,
          messenger,
          dataInvalidation,
        })

        await plugin.initialize?.(
          Object.freeze({
            scope,
            presenter: scope.presenter,
            remoteFakes: remoteFakeRegistrations.value,
            dataInvalidation: dataInvalidationRegistrations.value,
          })
        )

        presenterRegistrations.commit()
        remoteFakeRegistrations.commit()
        dataInvalidationRegistrations.commit()
        resolveInitialization(scope)
      } catch (error) {
        dataInvalidationRegistrations?.rollback()
        remoteFakeRegistrations?.rollback()
        presenterRegistrations?.rollback()
        if (state.initialization === initialization) {
          state.initialization = undefined
        }
        rejectInitialization(error)
      }
    })()

    return initialization
  }

  const authentication = createApplicationAuthentication(session, presenterRuntime.root, options.authentication)
  const preloads: ApplicationPreloads = { count: 0 }
  const signOutGuards = new Set<LeaveGuard>()
  let pendingSignOut: Promise<boolean> | null = null

  // An authorization that expires while the application is in use covers the application with the session
  // expired presentation until the user signs in again or signs out. Without that presentation it offers the
  // shared login challenge once; cancelling it leaves the session authentication-required, and a later 401
  // does not offer it again.
  let previousSessionStatus = session.status
  let expiryChallengePending = false
  let expiryChallenge: AbortController | null = null
  const unsubscribeExpiryStatus = session.subscribe(() => {
    const status = session.status
    if (previousSessionStatus === "signed-in" && status === "authentication-required") expiryChallengePending = true
    if (status !== "authentication-required") {
      expiryChallengePending = false
      expiryChallenge?.abort()
    }
    previousSessionStatus = status
  })

  async function coverExpiredSession(signal: AbortSignal) {
    // Without login there is nothing the cover could offer.
    if (options.authentication === false) return
    while (!signal.aborted && session.status === "authentication-required") {
      let choice
      try {
        choice = await presenterRuntime.root.present(
          sessionExpiredResponse,
          {
            username: session.user?.username,
            suppressPasswordManager: options.authentication?.suppressPasswordManager,
          },
          signal
        )
      } catch (error) {
        if (error instanceof PresenterUnhandledResponseError) await application.requestAuthentication(signal)
        return
      }
      // A guard that keeps the session, for unsaved changes, shows the cover again.
      if (choice === "sign-out") await application.signOut()
    }
  }
  // Session subscribed first, so its status already reflects this 401.
  const unsubscribeExpiryChallenge = remote.subscribeAuthenticationRequired(() => {
    if (!expiryChallengePending || preloads.count > 0 || session.status !== "authentication-required") return
    expiryChallengePending = false
    const controller = new AbortController()
    expiryChallenge = controller
    void coverExpiredSession(controller.signal)
      .catch(() => undefined)
      .finally(() => {
        if (expiryChallenge === controller) expiryChallenge = null
      })
  })

  async function signOutAfterGuards() {
    for (const guard of [...signOutGuards]) {
      if (!(await guard())) return false
    }
    session.signOut()
    return true
  }

  const application: Application = Object.freeze({
    branding: definition.branding,
    plugins: definition.plugins,
    presenter: presenterRuntime.root,
    remote,
    rootScope,
    session,
    storage,
    viewState,
    localization,
    telemetry: telemetryRuntime.telemetry,
    messenger,
    dataInvalidation,
    get authenticatedState() {
      return authenticatedRoot.state
    },
    subscribeAuthenticatedState: authenticatedRoot.subscribe,
    whenAuthenticated: authenticatedRoot.whenAuthenticated,
    retryAuthenticatedRoot: authenticatedRoot.retry,
    getPluginAvailability: authenticatedRoot.getPluginAvailability,
    isPluginAuthorized,
    activatePlugin,
    async requestAuthentication(signal?: AbortSignal) {
      const result = await authentication.request(signal)
      if (result !== "authenticated") return result
      await authenticatedRoot.whenAuthenticated()
      signal?.throwIfAborted()
      return session.status === "signed-in" ? "authenticated" : "unavailable"
    },
    signOut() {
      if (pendingSignOut != null) return pendingSignOut
      const operation = signOutAfterGuards()
      pendingSignOut = operation
      const clear = () => {
        if (pendingSignOut === operation) pendingSignOut = null
      }
      operation.then(clear, clear)
      return operation
    },
    registerSignOutGuard(guard: LeaveGuard) {
      // A wrapper keeps registrations of the same function independent.
      const registration: LeaveGuard = () => guard()
      signOutGuards.add(registration)
      return () => {
        signOutGuards.delete(registration)
      }
    },
    dispose() {
      unsubscribeExpiryStatus()
      unsubscribeExpiryChallenge()
      expiryChallenge?.abort()
      authentication.dispose()
      disposePromise ??= telemetryRuntime.telemetry.shutdown()
      return disposePromise
    },
  })
  applicationPreloads.set(application, preloads)
  return application
}

interface RegistrationTracker<T> {
  readonly value: T
  commit(): void
  rollback(): void
}

interface RegistrationLifecycle {
  track(unregister: () => void): void
  commit(): void
  rollback(): void
}

interface DataInvalidationBindingResolution {
  readonly dependencies: readonly DataDependency[]
  readonly errors: readonly unknown[]
}

interface DataInvalidationBindings {
  readonly registrar: DataInvalidationRegistrar
  resolve(messageType: string, payload: unknown): DataInvalidationBindingResolution
}

function createRegistrationLifecycle(): RegistrationLifecycle {
  const unregisters: Array<() => void> = []
  let state: "tracking" | "committed" | "rolled-back" = "tracking"

  return {
    track(unregister) {
      if (state === "tracking") {
        unregisters.push(unregister)
        return
      }
      if (state === "rolled-back") unregister()
    },
    commit() {
      if (state !== "tracking") return
      state = "committed"
      unregisters.length = 0
    },
    rollback() {
      if (state !== "tracking") return
      state = "rolled-back"
      for (let index = unregisters.length - 1; index >= 0; index -= 1) {
        unregisters[index]!()
      }
      unregisters.length = 0
    },
  }
}

function createDataInvalidationBindings(): DataInvalidationBindings {
  const bindings = new Map<string, Set<(payload: unknown) => readonly DataDependency[]>>()

  const registrar: DataInvalidationRegistrar = Object.freeze({
    invalidateOn<TPayload>(
      message: MessageDefinition<TPayload>,
      invalidates: DataDependencyInput | ((payload: Readonly<TPayload>) => DataDependencyInput)
    ) {
      const resolver =
        typeof invalidates === "function"
          ? (payload: unknown) =>
              normalizeDataDependencies(
                (invalidates as (value: Readonly<TPayload>) => DataDependencyInput)(payload as Readonly<TPayload>)
              )
          : () => normalizeDataDependencies(invalidates)
      let set = bindings.get(message.type)
      if (set == null) {
        set = new Set()
        bindings.set(message.type, set)
      }
      set.add(resolver)
      return () => {
        set?.delete(resolver)
        if (set?.size === 0) bindings.delete(message.type)
      }
    },
  })

  return {
    registrar,
    resolve(messageType, payload) {
      const dependencies: DataDependency[] = []
      const errors: unknown[] = []
      for (const resolver of bindings.get(messageType) ?? []) {
        try {
          dependencies.push(...resolver(payload))
        } catch (error) {
          errors.push(error)
        }
      }
      return { dependencies, errors }
    },
  }
}

function reportMessageInvalidationError(telemetry: ApplicationTelemetry, messageType: string, error: unknown) {
  telemetry.error("Message data invalidation failed.", error, {
    "florisoft.error.source": "message-invalidation",
    "florisoft.message.type": messageType,
  })
}

function trackPresenterRegistrations(presenter: Presenter): RegistrationTracker<Presenter> {
  const lifecycle = createRegistrationLifecycle()
  const trackedPresenter: Presenter = Object.freeze({
    notify<TResponse, TResult = void>(
      definition: PresentationResponseDefinition<TResponse, TResult>,
      response: TResponse,
      options?: PresentationNotificationOptions<TResult>
    ) {
      presenter.notify(definition, response, options)
    },
    present<TResponse, TResult = void>(
      definition: PresentationResponseDefinition<TResponse, TResult>,
      response: TResponse,
      signal?: AbortSignal
    ) {
      return presenter.present(definition, response, signal)
    },
    register<TResponse, TResult = void>(
      definition: PresentationResponseDefinition<TResponse, TResult>,
      handler: PresentationResponseHandler<TResponse, TResult>
    ) {
      const unregister = presenter.register(definition, handler)
      lifecycle.track(unregister)
      return unregister
    },
  })

  return {
    value: trackedPresenter,
    commit: lifecycle.commit,
    rollback: lifecycle.rollback,
  }
}

function trackDataInvalidationRegistrations(
  registrar: DataInvalidationRegistrar
): RegistrationTracker<DataInvalidationRegistrar> {
  const lifecycle = createRegistrationLifecycle()
  const trackedRegistrar: DataInvalidationRegistrar = Object.freeze({
    invalidateOn<TPayload>(
      message: MessageDefinition<TPayload>,
      invalidates: DataDependencyInput | ((payload: Readonly<TPayload>) => DataDependencyInput)
    ) {
      const unregister = registrar.invalidateOn(message, invalidates)
      lifecycle.track(unregister)
      return unregister
    },
  })

  return {
    value: trackedRegistrar,
    commit: lifecycle.commit,
    rollback: lifecycle.rollback,
  }
}

function trackRemoteFakeRegistrations(registrar: RemoteFakeRegistrar): RegistrationTracker<RemoteFakeRegistrar> {
  const lifecycle = createRegistrationLifecycle()
  const trackedRegistrar: RemoteFakeRegistrar = Object.freeze({
    register(...definitions: readonly RemoteFakeDefinition[]) {
      const unregister = registrar.register(...definitions)
      lifecycle.track(unregister)
      return unregister
    },
    registerRoot(...definitions: readonly RemoteFakeDefinition[]) {
      const unregister = registrar.registerRoot(...definitions)
      lifecycle.track(unregister)
      return unregister
    },
  })

  return {
    value: trackedRegistrar,
    commit: lifecycle.commit,
    rollback: lifecycle.rollback,
  }
}

function getScopeIds(plugin: ApplicationPlugin, plugins: ReadonlyMap<string, ApplicationPlugin>) {
  const ids: string[] = []
  let current: ApplicationPlugin | undefined = plugin

  while (current != null) {
    ids.push(current.id)
    current = current.parentId == null ? undefined : plugins.get(current.parentId)
  }

  return Object.freeze(ids)
}
